import { useEffect, useState } from 'react';
import { Typography, LinearProgress, Input, Tabs, SelectMultiple, Table, TableRow, TableCell } from '../../tailwindcomponents';
import { Button } from '../../tailwindcomponents';
import { Check, History, Search } from '../../components/icons';
import { ATAChapter, Request, RatingEnum } from '../../types/capabilityList';
import CapabilityListApi from '../../api/capabilityList';
import CapabilityRequest from './CapabilityRequest';
import RequestHistory from './RequestHistory';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducer';
import Print from './Print';
import { TableColumns } from '../../types/table';
import {useQueryTab} from '../../utils/usePersistedTab';

const shops = ["AVIONICS", "COMPONENT", "COMPOSITE", "ENGINE", "INTERIOR", "METAL"];
const sortTableColumns : TableColumns[] = [
    { label: "Part number", key: "part_number" },
    { label: "Part name", key: "part_name" },
    { label: "Class type", key: "class_type" },
    { label: "ATA", key: "ata_chapters" },
    { label: "Inspection/Tested", key: "is_inspection_test" },
    { label: "MOD´s", key: "is_mods" },
    { label: "OH", key: "is_oh" },
    { label: "Repair", key: "is_repair" },
    { label: "Rating", key: "rating" },
    { label: "Accepted Date", key: "accepted_date_text" },
    { label: "History", key: "", sortable: false },
    { label: "", key: "", sortable: false },
];

const sortTableColumnsEngineTab : TableColumns[] = [
    { label: "Engine (part number)", key: "part_number" },
    { label: "Component name", key: "component_name" },
    { label: "ATA", key: "ata_chapters" },
    { label: "Rating", key: "rating" },
    { label: "Task", key: "other_text" },
    { label: "Accepted Date", key: "accepted_date_text" },
    { label: "History", key: "", sortable: false },
    { label: "", key: "", sortable: false },
];

const initialRequest: Request = ({
    accepted_date: new Date(),
    part_name: "",
    part_number: "",
    dash_numbers: "",
    cmm_chapters: "",
    ata_chapters: "",
    description: "",
    special_training: "",
    manuals_needed_by_shop: "",
    is_inspection_test: false,
    is_repair: false,
    is_oh: false,
    is_mods: false,
    capability_sub_assys: [],
    capability_tools: [],
    shops: [],
    class_type: "",
    accepted_text: "",
    jira_link: "",
    rating: RatingEnum.Unknown,
    component_name: "",
    other_text: ""
});

const CapabilityList = () => {
    const { tabValue, setTabValue } = useQueryTab("tab", 0);
    const [filteredCapabilityRequests, setFilteredCapabilityRequests] = useState<Request[]>([]);
    const [capabilityRequests, setCapabilityRequests] = useState<Request[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [history, setHistory] = useState<boolean>(false);

    const [partSubAssyNumber, setPartSubAssyNumber] = useState<string>('');
    const [partName, setPartName] = useState<string>('');

    const [requestHistory, setRequestHistory] = useState<Request[]>([]);
    const [viewMode, setViewMode] = useState<boolean>(true);
    const [ATAChapters, setATAChapters] = useState<ATAChapter[]>([]);

    const [requestId, setRequestId] = useState<number>(-1);

    const store = useSelector((state: RootState) => state);
    const isCapabilityAdmin = store.user.me.user_clearence_groups.includes("Capability Edit");
    const isCapabilityUser = store.user.me.user_clearence_groups.includes("Capability View");
    const isShopLeader = store.user.me.user_clearence_groups.includes("Shop Team Leaders");

    const shopTabs = isCapabilityAdmin ? ["ALL SHOPS", "AVIONICS", "COMPONENT", "COMPOSITE", "ENGINE", "INTERIOR", "METAL", "A Rating" ,  "Create/Update"] : ["ALL SHOPS", "AVIONICS", "COMPONENT", "COMPOSITE", "ENGINE", "INTERIOR", "METAL", "A Rating"];
    isShopLeader && shopTabs.push("PRINT");

    const [request, setRequest] = useState<Request>(initialRequest);

    const [selectedShops, setSelectedShops] = useState<string[]>(shops);
    const [filteredShopsForPrint, setFilteredShopsForPrint] = useState<Request[]>(capabilityRequests);
    const [filteredAtaChapters, setFilteredAtaChapters] = useState<string[]>([]);
    const [filteredClassType, setFilteredClassType] = useState<string[]>([]);
    const [selectedClassType, setSelectedClassType] = useState<string[]>([]);
    const [classTypeOptions, setClassTypeOptions] = useState<string[]>([]);

    const onTabValueClicked = (tabVal: number) => {
        if (tabVal === 0) {
            fetchInitialData();
        }
        if (tabVal !== 8) {
            setRequestId(-1);
        }
        if (tabVal !== 0) {
            setHistory(false);
            setRequestHistory([]);
        }
        if (tabVal === 8) {
            setRequest(initialRequest);
        }
        if (tabVal > 0 && tabVal < 8) {
            onFilterRequests(partSubAssyNumber, partName, tabVal, filteredAtaChapters);
        }
    }

    const fetchInitialData = () => {
        setLoading(true);
        GetAllAtaChaptersRatings();
        CapabilityListApi.GetAllCapabilityRequests().then(res => {
            setCapabilityRequests(res.data);
            setFilteredShopsForPrint(res.data);
            
            const cRatings = res.data.filter((request) =>
                request.rating === RatingEnum.C);

            setFilteredCapabilityRequests(cRatings);

            setLoading(false);
        });

        CapabilityListApi.GetAllAtaChaptersRatings().then(res => {
            const uniqueRatings = new Set(res.data.map((obj: ATAChapter) => obj.rating));
            setClassTypeOptions(Array.from(uniqueRatings));
        });
        
    }

    const sortRequestsByShop = (tabVal: number) => {

        if(tabVal === 4){ // Engine tab
            const filteredCapabilityRequests = capabilityRequests.filter((request) =>
                request.shops.some((shop) => shop.shop_name === "ENGINE")
            );
            return filteredCapabilityRequests;
        }
        else if (tabVal === 0) {
            const filteredCapabilityRequests = capabilityRequests.filter((request) =>
                request.rating === RatingEnum.C
            );
            return filteredCapabilityRequests;
        }
        else if(tabVal === 7){ // A rating tab
            const filteredCapabilityRequests = capabilityRequests.filter((request) =>
                request.rating === RatingEnum.A
            );
            return filteredCapabilityRequests;
        }
        else if (tabVal > 0 && tabVal < 7) {
            const shopTab = shops[tabVal - 1];
            const filteredCapabilityRequests = capabilityRequests.filter((request) =>
                request.shops.some((shop) => shop.shop_name === shopTab)
            );
            return filteredCapabilityRequests;
        }
    }

    useEffect(() => {
        if (tabValue === 0) {
            fetchInitialData();
        }
    }, [tabValue]);

    const GetAllAtaChaptersRatings = () => {
        CapabilityListApi.GetAllAtaChaptersRatings().then(res => {
            setATAChapters(res.data);
            setATAChaptersRating(res.data);
        });
    }

    const setATAChaptersRating = (ataChapter: ATAChapter[]) => {
        if (request?.class_type === "" || request?.class_type === undefined) {
            const rating = ataChapter.find(s => s.ata_chapter === request?.ata_chapters)?.rating;
            if (rating !== "" && rating !== undefined) {
                setRequest((prevState: Request) => ({
                    ...prevState!,
                    class_type: rating,
                }));
            }
        }
    }

    const onFilterRequests = (partSubAssyNumber: string, partName: string, tabVal: number, ataChapter: string[]) => {
        const sortedByShop = sortRequestsByShop(tabVal);
        let filterByInputText: Request[] = sortedByShop !== undefined ? sortedByShop : [];
        if (ataChapter.length > 0) {
            const filteredbyAtaArray = filterByInputText.filter(obj => ataChapter.includes(obj.ata_chapters));
            filterByInputText = [...filteredbyAtaArray];
        }
        if (partName !== "" && partSubAssyNumber === "") {
            filterByInputText = filterByInputText.filter(s => s.part_name.toUpperCase().includes(partName.toUpperCase()));
        }
        else if (partSubAssyNumber !== "" && partName === "") {
            const filterSubAssys = filterByInputText.filter((request) =>
                request.capability_sub_assys.some((subAssy) => subAssy.part_number.includes(partSubAssyNumber))
            );
            const filterByPartNumbers = filterByInputText.filter(s => s.part_number.toUpperCase().includes(partSubAssyNumber.toUpperCase()));
            filterByInputText = [...filterSubAssys, ...filterByPartNumbers];
        }
        else if (partSubAssyNumber !== "" && partName !== "") {
            const filterSubAssys = filterByInputText.filter((request) =>
                request.capability_sub_assys.some((subAssy) => subAssy.part_number.includes(partSubAssyNumber))
            );
            const filterByPartNumbers = filterByInputText.filter(s => s.part_number.toUpperCase().includes(partSubAssyNumber.toUpperCase()));
            const filterByPartName = filterByInputText.filter(s => s.part_name.toUpperCase().includes(partName.toUpperCase()));
            filterByInputText = [...filterSubAssys, ...filterByPartNumbers, ...filterByPartName];
        }
        setFilteredCapabilityRequests(filterByInputText);
    }

    const onHistory = (requestId: number | undefined) => {
        if (requestId !== undefined) {
            setLoading(true);
            CapabilityListApi.GetRequestHistoryById(requestId).then(res => {
                setRequestHistory(res.data);
                setLoading(false);
                setHistory(true);
                setViewMode(true);
            });
        }
    }

    const onSetEditRequest = (rowRequestId: number) => {
        setLoading(true);
        GetAllAtaChaptersRatings();
        CapabilityListApi.GetRequestById(rowRequestId).then(res => {
            setRequest(res.data);
            setLoading(false);
            setRequestId(rowRequestId);
            setTabValue(8);
        });
    }

    const generateTableBody = (row: Request, index: number) => {
        return (
            <TableRow >
                <TableCell>{row.part_number} </TableCell>
                <TableCell>{row.part_name} </TableCell>
                <TableCell>{row.class_type} </TableCell>
                <TableCell>{row.ata_chapters} </TableCell>
                <TableCell>{row.is_inspection_test && <Check className="text-green-600" />} </TableCell>
                <TableCell>{row.is_mods && <Check className="text-green-600" />} </TableCell>
                <TableCell>{row.is_oh && <Check className="text-green-600" />} </TableCell>
                <TableCell>{row.is_repair && <Check className="text-green-600" />} </TableCell>
                <TableCell>{RatingEnum[row.rating]} </TableCell>
                <TableCell>{row.accepted_date_text} </TableCell>
                <TableCell>{<Button variant="text" onClick={() => { onHistory(row.request_id) }}><History color='success' /></Button>}</TableCell>
                <TableCell><Search className="cursor-pointer" onClick={() => { row.request_id !== undefined && onSetEditRequest(row.request_id) }} /></TableCell>
            </TableRow>
        )
    }

    const generateTableBodyEngineTab = (row: Request, index: number) => {
        return (
            <TableRow >
                <TableCell>{row.part_number}</TableCell>
                <TableCell>{row.component_name} </TableCell>
                <TableCell>{row.ata_chapters} </TableCell>
                <TableCell>{RatingEnum[row.rating]} </TableCell>
                <TableCell>{row.other_text} </TableCell>
                <TableCell>{row.accepted_date_text} </TableCell>
                <TableCell>{<Button variant="text" onClick={() => { onHistory(row.request_id) }}><History color='success' /></Button>}</TableCell>
                <TableCell><Search className="cursor-pointer" onClick={() => { row.request_id !== undefined && onSetEditRequest(row.request_id) }} /></TableCell>
            </TableRow>
        )
    }

    const filterByShopOptions = (e: string[]) => {
        if (e.length === shops.length) {
            setFilteredShopsForPrint(capabilityRequests);
        }
        else {
            const matchingRequests = capabilityRequests.filter(request =>
                request.shops.some(shop => e.includes(shop.shop_name))
            );
            setFilteredShopsForPrint(matchingRequests);
        }
        setSelectedShops(e);
    }

    const filterByClassTypeOptions = (e: string[]) => {
        if (e.length === 0) {
            // If no checkbox is selected, show all data
            setFilteredCapabilityRequests(capabilityRequests);
            setSelectedClassType([]);
            setFilteredClassType([]);
            return;
        }
    
        if (e.length === classTypeOptions.length) {
            // If all options are selected, show all data
            setFilteredCapabilityRequests(capabilityRequests);
        } else {
            // Otherwise, filter the data
            const matchingRequests = capabilityRequests.filter(request =>
                e.includes(request.class_type)
            );
            setFilteredCapabilityRequests(matchingRequests);
        }
    
        setSelectedClassType(e);
        setFilteredClassType(e);
    };
    


    const onClearFilters = () => {
        setPartSubAssyNumber('');
        setPartName('');
        setFilteredAtaChapters([]);
        setFilteredCapabilityRequests(capabilityRequests);
    }

    return (
        <div>
            {(isCapabilityUser || isCapabilityAdmin) ?
                <div>
                    {
                        tabValue !== 9 &&
                        <Tabs
                            tabNames={shopTabs}
                            tabValue={tabValue}
                            onChangeTabValue={(tabValue: number) => { setTabValue(tabValue); onTabValueClicked(tabValue) }}
                        />
                    }
                    {loading && <LinearProgress />}
                    {capabilityRequests.length > 0 &&
                        ((tabValue !== 8 && tabValue !== 9) && !history) &&
                        <div>
                            <div className="ds-filter-bar">

                                {/* Search by Part or Sub Assy Number */}
                                <Input
                                    multiline={false}
                                    help_text=""
                                    label_text="Search by part or sub assy number"
                                    value={partSubAssyNumber}
                                    onTextChange={(text: string) => {
                                        setPartSubAssyNumber(text);
                                        onFilterRequests(text, partName, tabValue, filteredAtaChapters);
                                    }}
                                    type="text"
                                    width={100}
                                />

                                {/* Part Name */}
                                <Input
                                    multiline={false}
                                    help_text=""
                                    label_text="Part name"
                                    value={partName}
                                    onTextChange={(text: string) => {
                                        setPartName(text);
                                        onFilterRequests(partSubAssyNumber, text, tabValue, filteredAtaChapters);
                                    }}
                                    type="text"
                                    width={100}
                                />

                                {/* Filter by ATA Chapter */}
                                <SelectMultiple
                                    options={[...[''], ...ATAChapters.map((obj: ATAChapter) => obj.ata_chapter)]}
                                    onSelectedItem={(e: any) => {
                                        setFilteredAtaChapters(e.target.value);
                                        onFilterRequests(partSubAssyNumber, partName, tabValue, e.target.value);
                                    }}
                                    widthPercentage={true}
                                    selectedItemsString={filteredAtaChapters}
                                    width={100}
                                    filter_by_text="ATA chapter"
                                />

                                {/* Choose Shops to Print */}
                                <SelectMultiple
                                    options={shops}
                                    required
                                    onSelectedItem={(e: any) => filterByShopOptions(e.target.value)}
                                    selectedItemsString={selectedShops}
                                    width={100}
                                    widthPercentage
                                    filter_by_text="Choose shops to print"
                                />

                                {/* Filter by Class Type */}
                                <SelectMultiple
                                    options={classTypeOptions}
                                    required
                                    onSelectedItem={(e: any) => filterByClassTypeOptions(e.target.value)}
                                    selectedItemsString={selectedClassType}
                                    width={100}
                                    widthPercentage
                                    filter_by_text="Filter by class type"
                                />

                                {/* Clear Filters Button */}
                                <div className="ds-filter-actions">
                                    <Button variant="contained" color="primary" size="small" onClick={onClearFilters}>
                                        Clear filters
                                    </Button>
                                </div>

                            </div>

                            {tabValue !== 4 &&
                            <div>
                                <Table
                                    sortTableColumns={sortTableColumns}
                                    rows={filteredCapabilityRequests}
                                    generateBody={(row: Request, index: number) => generateTableBody(row, index)}
                                    pagination={true}
                                    defaultRowsPerPage={100}
                                    skipIndexCol={true}
                                />
                            </div>
                            }

                            {tabValue === 4 &&
                            <div>
                                <Table
                                    sortTableColumns={sortTableColumnsEngineTab}
                                    rows={filteredCapabilityRequests}
                                    generateBody={(row: Request, index: number) => generateTableBodyEngineTab(row, index)}
                                    pagination={true}
                                    defaultRowsPerPage={100}
                                    skipIndexCol={true}
                                />
                            </div>
                            }
                        </div>
                    }
                    {history &&
                        <RequestHistory
                            requestHistory={requestHistory}
                            setHistory={setHistory}
                            setRequestHistory={setRequestHistory}
                            ATAChapters={ATAChapters}
                            setViewMode={setViewMode}
                            setRequest={setRequest}
                            setTabValue={setTabValue}
                        />
                    }
                    {tabValue === 8 &&
                        <CapabilityRequest
                            isCapabilityAdmin={isCapabilityAdmin}
                            requestId={requestId}
                            request={request}
                            setRequest={setRequest}
                            setViewMode={setViewMode}
                            viewMode={viewMode}
                            ATAChapters={ATAChapters}
                            setTabValue={setTabValue}
                        />
                    }
                    {(tabValue === 9) &&
                        <Print filteredShopsForPrint={filteredShopsForPrint} setTabValue={setTabValue} />
                    }
                </div>
                :
                <Typography variant='h1' text={`You are not authorized to view this page`} align='center' />
            }
        </div>
    )
}

export default CapabilityList;